import React, { useState, useEffect, useRef } from 'react';
import { Eye, FileText, Upload, Sparkles, Plus, Languages, RefreshCw, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { RichTextEditor } from '../RichTextEditor';
import { toast } from 'sonner';

const ACCEPTED_EXTS = ['.pdf', '.docx', '.txt', '.rtf', '.png', '.jpg', '.jpeg', '.webp'];
const MAX_FILE_BYTES = 25 * 1024 * 1024;

/**
 * Validate a dropped/selected file. Returns the file when valid, null otherwise
 * (with a toast already shown to the user).
 */
function _validateImportFile(file, language) {
  if (!file) return null;
  const name = (file.name || '').toLowerCase();
  if (!ACCEPTED_EXTS.some(ext => name.endsWith(ext))) {
    if (name.endsWith('.doc')) {
      toast.error(language === 'pt'
        ? 'Ficheiros .doc antigos não são suportados. Guarde como .docx ou PDF.'
        : 'Old .doc files not supported. Save as .docx or PDF.');
    } else {
      toast.error(language === 'pt'
        ? 'Aceitamos PDF, Word (.docx), TXT, RTF ou imagens (PNG/JPG/WEBP)'
        : 'We accept PDF, Word (.docx), TXT, RTF or images (PNG/JPG/WEBP)');
    }
    return null;
  }
  if (file.size > MAX_FILE_BYTES) {
    toast.error(language === 'pt'
      ? 'Ficheiro demasiado grande (máx. 25MB)'
      : 'File too large (max 25MB)');
    return null;
  }
  return file;
}

/**
 * AI Import Dialog (PDF/Word/TXT/RTF/Image upload — drag-and-drop or click).
 *
 * Behaviour:
 *  - Drop a file anywhere in the drop zone to load OR replace the current one
 *  - Click the zone (or "Substituir ficheiro" button) to open the file picker
 *  - "Remover ficheiro" clears the selection
 */
export const ImportDialog = ({
  open, onOpenChange, language, t,
  importFile, setImportFile,
  importing, handleImportCV,
}) => {
  const [dragOver, setDragOver] = useState(false);
  // Counter pattern: dragenter/leave fire for every child element. We only
  // hide the drop-over visual when the counter reaches 0 again.
  const dragCounter = useRef(0);
  const inputId = 'cv-file-upload';

  const handleFileChosen = (file) => {
    const valid = _validateImportFile(file, language);
    if (valid) setImportFile(valid);
  };

  // While the dialog is open, prevent the browser from navigating to dropped
  // files outside our zone (default Edge/Chrome behaviour). Without this, a
  // misaimed drop would replace the whole tab with the PDF.
  useEffect(() => {
    if (!open) return undefined;
    const prevent = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
      dragCounter.current = 0;
      setDragOver(false);
    };
  }, [open]);

  const onZoneDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only count drags carrying files (ignores text/url drags)
    if (e.dataTransfer?.types?.includes('Files')) {
      dragCounter.current += 1;
      setDragOver(true);
    }
  };

  const onZoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Hint the OS that we accept a file copy
    if (e.dataTransfer) {
      try { e.dataTransfer.dropEffect = 'copy'; } catch { /* read-only in some browsers */ }
    }
  };

  const onZoneDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setDragOver(false);
  };

  const onZoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragOver(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) handleFileChosen(dropped);
  };

  const openPicker = () => {
    if (importing) return;
    document.getElementById(inputId)?.click();
  };

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="import-dialog">
      <DialogHeader>
        <DialogTitle>{language === 'pt' ? 'Importar CV' : 'Import CV'}</DialogTitle>
        <DialogDescription>
          {language === 'pt'
            ? 'Arraste o ficheiro para a área abaixo ou clique para escolher. PDF, Word (.docx), TXT, RTF ou imagem (PNG/JPG/WEBP). O texto é extraído (com OCR para imagens e PDFs digitalizados) e processado automaticamente pela IA.'
            : 'Drop a file in the area below or click to choose. PDF, Word (.docx), TXT, RTF or image (PNG/JPG/WEBP). Text is extracted (OCR for images and scanned PDFs) and processed automatically by AI.'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div
          onDragEnter={onZoneDragEnter}
          onDragOver={onZoneDragOver}
          onDragLeave={onZoneDragLeave}
          onDrop={onZoneDrop}
          onClick={openPicker}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !importing) openPicker(); }}
          data-testid="cv-file-dropzone"
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer select-none ${
            dragOver
              ? 'border-[#C9A84C] bg-[#C9A84C]/10 scale-[1.01]'
              : importFile
                ? 'border-[#1E3C3C] bg-[#1E3C3C]/5'
                : 'border-gray-300 hover:border-[#1E3C3C] hover:bg-[#f0fdfa]'
          }`}
        >
          {/* Transparent overlay that swallows pointer events from children while a
              drag is in progress — this is the Edge/Chrome fix: ensures the parent
              receives drop/dragover events instead of nested icons/text. */}
          {dragOver && (
            <div
              className="absolute inset-0 z-10 pointer-events-auto rounded-lg"
              onDragOver={onZoneDragOver}
              onDragLeave={onZoneDragLeave}
              onDrop={onZoneDrop}
              data-testid="cv-file-dropzone-overlay"
            />
          )}
          <input
            type="file"
            accept=".pdf,.docx,.txt,.rtf,.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            id={inputId}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileChosen(file);
              // Reset value so the same file can be re-selected after removal
              e.target.value = '';
            }}
            data-testid="cv-file-upload-input"
          />
          {importFile ? (
            <div className="space-y-2" data-testid="selected-file-info">
              <FileText className="w-12 h-12 mx-auto text-[#1E3C3C]" />
              <p className="font-medium text-[#0f172a] break-all px-2">{importFile.name}</p>
              <p className="text-sm text-[#64748b]">{(importFile.size / 1024).toFixed(1)} KB</p>
              <p className="text-xs text-[#94a3b8]">
                {language === 'pt'
                  ? 'Arraste outro ficheiro para substituir ou use o botão abaixo.'
                  : 'Drop another file to replace or use the button below.'}
              </p>
              <div className="flex justify-center gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openPicker}
                  data-testid="replace-file-btn"
                  className="border-[#1E3C3C] text-[#1E3C3C] hover:bg-[#f0fdfa]"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  {language === 'pt' ? 'Substituir ficheiro' : 'Replace file'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setImportFile(null)}
                  className="text-red-500 hover:text-red-700"
                  data-testid="remove-file-btn"
                >
                  {language === 'pt' ? 'Remover' : 'Remove'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className={`w-12 h-12 mx-auto transition-colors ${dragOver ? 'text-[#C9A84C]' : 'text-[#94a3b8]'}`} />
              <p className="font-medium text-[#0f172a]">
                {dragOver
                  ? (language === 'pt' ? 'Largue para carregar' : 'Drop to upload')
                  : (language === 'pt' ? 'Arraste o ficheiro ou clique para escolher' : 'Drop a file here or click to choose')}
              </p>
              <p className="text-sm text-[#64748b]">
                {language === 'pt'
                  ? 'PDF, Word (.docx), TXT, RTF ou imagem (PNG/JPG/WEBP) — Máximo 25MB'
                  : 'PDF, Word (.docx), TXT, RTF or image (PNG/JPG/WEBP) — Maximum 25MB'}
              </p>
            </div>
          )}
        </div>
      </div>
      <DialogFooter className="flex gap-2">
        <Button variant="outline" onClick={() => { onOpenChange(false); setImportFile(null); }}>{t('cancel')}</Button>
        <Button onClick={handleImportCV} disabled={importing || !importFile} className="btn-primary" data-testid="process-cv-btn">
          {importing ? <div className="loading-spinner w-4 h-4 mr-2"></div> : <Sparkles className="w-4 h-4 mr-2" />}
          {importing
            ? (language === 'pt' ? 'A processar...' : 'Processing...')
            : (language === 'pt' ? 'Processar com IA' : 'Process with AI')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
};
export const ManualImportDialog = ({
  open, onOpenChange, language, t,
  manualImportText, setManualImportText,
  manualImporting, handleManualImportCV,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-import-dialog">
      <DialogHeader>
        <DialogTitle>{language === 'pt' ? 'Importar CV Manualmente' : 'Import CV Manually'}</DialogTitle>
        <DialogDescription>
          {language === 'pt'
            ? 'Cole ou escreva o conteúdo do CV. Pode formatar o texto com negrito, listas e escolher o tipo de letra. A IA irá processar e preencher os campos automaticamente.'
            : 'Paste or write the CV content. You can format text with bold, lists and choose the font. AI will process and fill in the fields automatically.'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <RichTextEditor
          value={manualImportText}
          onChange={setManualImportText}
          language={language}
          placeholder={language === 'pt'
            ? 'Cole aqui o conteúdo do CV...\n\nExemplo:\nJoão Silva\nEngenheiro de Software Sénior\n\nExperiência:\n• Empresa XYZ (2019-2024)\n  - Desenvolvimento de microserviços em Java\n  - Liderança de equipa de 5 pessoas'
            : 'Paste CV content here...\n\nExample:\nJohn Smith\nSenior Software Engineer\n\nExperience:\n• Company XYZ (2019-2024)\n  - Microservices development in Java\n  - Team lead of 5 people'}
          minHeight="280px"
        />
        <p className="text-xs text-[#94a3b8]">
          {language === 'pt'
            ? 'Dica: Pode copiar texto de um documento Word, PDF ou email e colar aqui. A formatação será preservada.'
            : 'Tip: You can copy text from a Word document, PDF or email and paste here. Formatting will be preserved.'}
        </p>
      </div>
      <DialogFooter className="flex gap-2">
        <Button variant="outline" onClick={() => { onOpenChange(false); setManualImportText(''); }} data-testid="manual-import-cancel-btn">{t('cancel')}</Button>
        <Button onClick={handleManualImportCV} disabled={manualImporting || !manualImportText.trim()} className="btn-primary" data-testid="manual-import-process-btn">
          {manualImporting ? <div className="loading-spinner w-4 h-4 mr-2"></div> : <Sparkles className="w-4 h-4 mr-2" />}
          {manualImporting
            ? (language === 'pt' ? 'A processar...' : 'Processing...')
            : (language === 'pt' ? 'Processar com IA' : 'Process with AI')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/**
 * Bulk Add Competencies Dialog
 */
export const BulkCompetenciesDialog = ({
  open, onOpenChange, language, t,
  filteredCompetencies, cv,
  selectedBulkComps, setSelectedBulkComps, toggleBulkCompetency,
  handleAddBulkCompetencies,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="bulk-comp-dialog">
      <DialogHeader>
        <DialogTitle>{language === 'pt' ? 'Adicionar Várias Competências' : 'Add Multiple Competencies'}</DialogTitle>
        <DialogDescription>{language === 'pt' ? 'Selecione as competências para adicionar' : 'Select competencies to add'}</DialogDescription>
      </DialogHeader>
      <div className="max-h-[400px] overflow-y-auto space-y-2">
        {filteredCompetencies.map((comp) => {
          const alreadyAdded = cv.main_competencies.some(c => c.competency_id === comp.id);
          return (
            <div key={comp.id} className={`flex items-center gap-3 p-3 rounded-lg ${alreadyAdded ? 'bg-gray-100 opacity-50' : 'bg-[#f8fafc] hover:bg-[#f0fdfa]'}`}>
              <Checkbox id={`bulk-${comp.id}`} checked={selectedBulkComps.includes(comp.id)} onCheckedChange={() => !alreadyAdded && toggleBulkCompetency(comp.id)} disabled={alreadyAdded} />
              <Label htmlFor={`bulk-${comp.id}`} className={`cursor-pointer flex-1 ${alreadyAdded ? 'text-gray-400' : ''}`}>
                {language === 'pt' ? comp.name_pt : comp.name_en}
                {alreadyAdded && <span className="ml-2 text-xs text-gray-400">({language === 'pt' ? 'já adicionada' : 'already added'})</span>}
              </Label>
              <span className="text-xs text-[#64748b]">{comp.category}</span>
            </div>
          );
        })}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { onOpenChange(false); setSelectedBulkComps([]); }}>{t('cancel')}</Button>
        <Button onClick={handleAddBulkCompetencies} disabled={selectedBulkComps.length === 0} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          {language === 'pt' ? `Adicionar ${selectedBulkComps.length}` : `Add ${selectedBulkComps.length}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/**
 * PDF Preview Dialog (inline iframe)
 */
export const PreviewDialog = ({
  open, onOpenChange, language,
  previewUrl,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="pdf-preview-dialog">
      <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#1E3C3C]/10">
        <DialogTitle className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#1E3C3C]" />
            {language === 'pt' ? 'Pré-visualização do CV' : 'CV Preview'}
          </span>
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-normal text-[#1E3C3C] hover:underline mr-8"
              data-testid="pdf-preview-open-new-tab"
            >
              {language === 'pt' ? 'Abrir em separador' : 'Open in new tab'}
            </a>
          )}
        </DialogTitle>
      </DialogHeader>
      <div className="flex-1 bg-[#f4f4f5] overflow-hidden">
        {previewUrl ? (
          <iframe src={previewUrl} title="CV Preview" className="w-full h-full border-0" data-testid="pdf-preview-iframe" />
        ) : (
          <div className="flex items-center justify-center h-full text-[#64748b]">
            <div className="loading-spinner w-8 h-8"></div>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

/**
 * Translate CV Dialog — asks user which languages to generate duplicates in.
 */
const TRANSLATE_LANGS = [
  { code: 'pt', label_pt: 'Português', label_en: 'Portuguese', flag: '🇵🇹' },
  { code: 'en', label_pt: 'Inglês', label_en: 'English', flag: '🇬🇧' },
  { code: 'fr', label_pt: 'Francês', label_en: 'French', flag: '🇫🇷' },
  { code: 'de', label_pt: 'Alemão', label_en: 'German', flag: '🇩🇪' },
];

export const TranslateCVDialog = ({
  open, onOpenChange, language, t,
  sourceLang, selectedLangs, setSelectedLangs,
  translating, handleTranslate,
}) => {
  const isPT = language === 'pt';
  const toggle = (code) => {
    setSelectedLangs(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const candidates = TRANSLATE_LANGS.filter(l => l.code !== sourceLang);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="translate-cv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-[#1E3C3C]" />
            {isPT ? 'Gerar CV noutras línguas?' : 'Generate CV in other languages?'}
          </DialogTitle>
          <DialogDescription>
            {isPT
              ? 'O CV foi gravado. Selecione os idiomas para os quais quer criar versões traduzidas (será criado um CV duplicado por idioma).'
              : 'CV saved. Pick which languages you want translated copies for (one duplicate CV per language will be created).'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="translate-lang-list">
          {candidates.map(l => (
            <label
              key={l.code}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedLangs.includes(l.code) ? 'border-[#1E3C3C] bg-[#1E3C3C]/5' : 'border-gray-200 hover:bg-[#f0fdfa]'
              }`}
              data-testid={`translate-lang-${l.code}`}
            >
              <Checkbox checked={selectedLangs.includes(l.code)} onCheckedChange={() => toggle(l.code)} />
              <span className="text-xl leading-none">{l.flag}</span>
              <span className="flex-1 font-medium text-[#0f172a]">{isPT ? l.label_pt : l.label_en}</span>
              <span className="text-xs text-[#64748b] uppercase">{l.code}</span>
            </label>
          ))}
        </div>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); setSelectedLangs([]); }}
            disabled={translating}
            data-testid="translate-skip-btn"
          >
            {isPT ? 'Saltar' : 'Skip'}
          </Button>
          <Button
            onClick={handleTranslate}
            disabled={translating || selectedLangs.length === 0}
            className="btn-primary"
            data-testid="translate-confirm-btn"
          >
            {translating
              ? <div className="loading-spinner w-4 h-4 mr-2"></div>
              : <Languages className="w-4 h-4 mr-2" />}
            {translating
              ? (isPT ? 'A traduzir…' : 'Translating…')
              : (isPT ? `Traduzir (${selectedLangs.length})` : `Translate (${selectedLangs.length})`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Export Word Language Dialog — asks user which language to export the Word document in.
 */
const EXPORT_LANGS = [
  { code: 'pt', label_pt: 'Português', label_en: 'Portuguese', flag: '🇵🇹' },
  { code: 'en', label_pt: 'Inglês', label_en: 'English', flag: '🇬🇧' },
  { code: 'fr', label_pt: 'Francês', label_en: 'French', flag: '🇫🇷' },
  { code: 'de', label_pt: 'Alemão', label_en: 'German', flag: '🇩🇪' },
  { code: 'es', label_pt: 'Espanhol', label_en: 'Spanish', flag: '🇪🇸' },
];

export const ExportLangDialog = ({
  open, onOpenChange, language,
  defaultLang, exporting, onConfirm,
}) => {
  const isPT = language === 'pt';
  const [selected, setSelected] = useState(defaultLang || 'pt');

  useEffect(() => {
    if (open) setSelected(defaultLang || 'pt');
  }, [open, defaultLang]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-lang-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#1E3C3C]" />
            {isPT ? 'Em que idioma quer exportar?' : 'Which language to export?'}
          </DialogTitle>
          <DialogDescription>
            {isPT
              ? 'Selecione o idioma do ficheiro Word a descarregar.'
              : 'Select the language of the Word file to download.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="export-lang-list">
          {EXPORT_LANGS.map(l => (
            <label
              key={l.code}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selected === l.code ? 'border-[#1E3C3C] bg-[#1E3C3C]/5' : 'border-gray-200 hover:bg-[#f0fdfa]'
              }`}
              data-testid={`export-lang-${l.code}`}
            >
              <input
                type="radio"
                name="export-lang"
                checked={selected === l.code}
                onChange={() => setSelected(l.code)}
                className="w-4 h-4 accent-[#1E3C3C]"
                data-testid={`export-lang-radio-${l.code}`}
              />
              <span className="text-xl leading-none">{l.flag}</span>
              <span className="flex-1 font-medium text-[#0f172a]">{isPT ? l.label_pt : l.label_en}</span>
              <span className="text-xs text-[#64748b] uppercase">{l.code}</span>
            </label>
          ))}
        </div>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
            data-testid="export-lang-cancel-btn"
          >
            {isPT ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={exporting}
            className="btn-primary"
            data-testid="export-lang-confirm-btn"
          >
            {exporting
              ? <div className="loading-spinner w-4 h-4 mr-2"></div>
              : <Download className="w-4 h-4 mr-2" />}
            {exporting
              ? (isPT ? 'A exportar…' : 'Exporting…')
              : (isPT ? 'Exportar' : 'Export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
